/**
 * V3.1q-fix8 — Page publique de prise de RDV téléphonique.
 *
 * Accédée via /book/call/:token (lien envoyé par email aux bénéficiaires
 * en statut "Sans réponse"). Permet de choisir un créneau de 15 min et
 * crée automatiquement un event dans l'agenda société.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Loader2, CheckCircle2, AlertTriangle, Phone, Clock, Calendar,
} from 'lucide-react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function BookCall() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [phone, setPhone] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);
  useEffect(() => { document.title = 'Réserver un rappel — Reno Resolve'; }, []);

  const load = async () => {
    try {
      const r = await axios.get(`${API_BASE}/cee/callback/${token}`);
      setState({ loading: false, data: r.data, error: null });
      setPhone(r.data?.beneficiaire?.telephone || '');
      if (r.data?.status === 'booked') {
        setConfirmed({
          start: r.data.booked_slot_start, end: r.data.booked_slot_end,
        });
      } else {
        // Default : premier jour disponible
        const firstDay = r.data?.slots?.[0]?.day_key || null;
        setSelectedDay(firstDay);
      }
    } catch (e) {
      setState({ loading: false, data: null,
        error: e?.response?.data?.detail || 'Lien invalide ou expiré' });
    }
  };

  const confirm = async () => {
    if (!selectedSlot) { toast.error('Choisissez un créneau'); return; }
    setConfirming(true);
    try {
      const r = await axios.post(`${API_BASE}/cee/callback/${token}`, {
        slot_start: selectedSlot.start,
        slot_end: selectedSlot.end,
        telephone: phone.trim() || undefined,
      });
      if (r.data?.success) {
        setConfirmed({ start: r.data.booked_slot_start, end: r.data.booked_slot_end });
        toast.success('Rappel confirmé');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur confirmation');
      if (e?.response?.status === 409) load();
    } finally { setConfirming(false); }
  };

  // ------------------------------------------------- Loading / error states
  if (state.loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#004AAD]" />
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-200 max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-slate-900 mb-1">Lien invalide</h2>
          <p className="text-slate-500 text-sm">{state.error}</p>
        </div>
      </div>
    );
  }

  if (confirmed) {
    const startDate = new Date(confirmed.start);
    const fmt = startDate.toLocaleString('fr-FR', {
      weekday: 'long', day: 'numeric', month: 'long',
      hour: '2-digit', minute: '2-digit',
    });
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200 max-w-lg text-center"
             data-testid="booking-confirmed">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-9 h-9 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Rappel confirmé !</h2>
          <p className="text-slate-600 text-sm mb-1">Un conseiller Reno Resolve vous rappellera le :</p>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 my-4 text-emerald-900 font-bold text-lg">
            {fmt}
          </div>
          <p className="text-slate-500 text-sm">Durée estimée : 15 minutes.</p>
          <p className="text-xs text-slate-400 mt-6">
            Un email de confirmation vous a été envoyé.
          </p>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------- Main form
  const d = state.data;
  const b = d.beneficiaire || {};
  const slots = d.slots || [];
  const dayGroups = {};
  slots.forEach((s) => {
    if (!dayGroups[s.day_key]) dayGroups[s.day_key] = { label: s.day_label, slots: [] };
    dayGroups[s.day_key].slots.push(s);
  });
  const dayKeys = Object.keys(dayGroups);
  const activeDay = selectedDay || dayKeys[0];
  const activeSlots = (dayGroups[activeDay] || {}).slots || [];

  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      <div className="bg-gradient-to-br from-[#004AAD] to-[#003580] text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center gap-4 flex-wrap">
          <div className="bg-white/95 rounded-xl px-4 py-2.5 shadow-md">
            <img src="/logo.png" alt="Reno Resolve" className="h-9 w-auto" />
          </div>
          <div className="flex items-center gap-2">
            <Phone className="w-6 h-6 text-blue-100" />
            <div>
              <h1 className="text-lg font-bold leading-tight">Réserver mon rappel téléphonique</h1>
              <p className="text-blue-100 text-xs">
                Créneaux de 15 minutes · lundi au samedi, 9h-18h
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        {/* Résumé */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-sm text-slate-700 leading-relaxed">
            Bonjour{b.nom ? ` ${b.nom}` : ''}, nous avons tenté de vous joindre
            suite à votre intérêt pour notre dispositif d'aide à la rénovation.
            Choisissez le créneau qui vous convient le mieux ci-dessous — un
            conseiller vous rappellera au numéro renseigné.
          </p>
          <div className="mt-4">
            <label className="text-xs font-medium text-slate-600 block mb-1">
              Numéro de téléphone pour le rappel
            </label>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="06 12 34 56 78"
              className="max-w-xs"
              data-testid="booking-phone-input"
            />
          </div>
        </div>

        {/* Jours disponibles */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-5 h-5 text-[#004AAD]" />
            <h2 className="font-bold text-slate-900">Jours disponibles</h2>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-2 px-2" data-testid="booking-days">
            {dayKeys.map((k) => {
              const dg = dayGroups[k];
              const active = activeDay === k;
              return (
                <button
                  key={k}
                  onClick={() => { setSelectedDay(k); setSelectedSlot(null); }}
                  className={`flex-shrink-0 px-4 py-3 rounded-lg border text-sm text-left min-w-[140px] transition-colors ${active ? 'border-[#004AAD] bg-blue-50 font-semibold text-[#004AAD]' : 'border-slate-200 hover:bg-slate-50 text-slate-700'}`}
                  data-testid={`booking-day-${k}`}
                >
                  <div className="text-xs text-slate-500">{dg.label.split(' ')[0]}</div>
                  <div className="text-base leading-tight">
                    {dg.label.split(' ').slice(1).join(' ')}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{dg.slots.length} créneaux</div>
                </button>
              );
            })}
          </div>

          {/* Créneaux horaires */}
          <div className="mt-5">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-slate-500" />
              <span className="text-sm text-slate-700 font-medium">
                Horaires du {dayGroups[activeDay]?.label || '—'}
              </span>
            </div>
            {activeSlots.length === 0 ? (
              <p className="text-sm text-slate-500 italic">Aucun créneau disponible ce jour.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {activeSlots.map((s) => {
                  const isSelected = selectedSlot?.start === s.start;
                  return (
                    <button
                      key={s.start}
                      onClick={() => setSelectedSlot(s)}
                      className={`py-2 rounded-md border text-sm transition-colors ${isSelected ? 'bg-[#004AAD] text-white border-[#004AAD] font-semibold' : 'border-slate-200 hover:bg-slate-50 text-slate-700'}`}
                      data-testid={`booking-slot-${s.start}`}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Confirmation */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm">
            {selectedSlot ? (
              <>
                <div className="text-xs text-slate-500">Créneau sélectionné</div>
                <div className="font-bold text-slate-900">
                  {dayGroups[selectedSlot.day_key]?.label} · {selectedSlot.label}
                </div>
              </>
            ) : (
              <div className="text-slate-500 text-sm italic">
                Sélectionnez un jour puis un horaire.
              </div>
            )}
          </div>
          <Button
            onClick={confirm}
            disabled={confirming || !selectedSlot}
            className="bg-[#004AAD] hover:bg-[#003580]"
            data-testid="booking-confirm-btn"
          >
            {confirming ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Confirmation…</>
            ) : (
              <>Confirmer le rappel</>
            )}
          </Button>
        </div>

        <p className="text-[11px] text-center text-slate-400">
          Vos données sont utilisées uniquement pour organiser cet appel
          (RGPD · Reno Resolve).
        </p>
      </div>
    </div>
  );
}
